// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : templating reducer
// =============================================================================
// ---Third-party imports
import now from 'performance-now'

// ---Framework imports
import {cleanPathPrefix} from 'uix/deploy'
import {decodeRequestWithAuth, decodeDomain} from 'uix/routing'
import log from 'uix/logging'
import {
  keys,
  has,
  isEmpty,
  entries,
  dict,
  get,
  size,
  sum,
  orderBy,
  sorted,
  fromPairs,
  mapItems,
  filterItems,
  isInteger,
} from 'ldash'
import {getRunId} from 'uix/selectors'
import {hashObj} from 'uix/utils'
import {GENERATOR, handleAction, handleActions} from 'uix/actions'
import {DEFAULT_THEME, CSS_VAR_NAMES} from 'uix/styles/variables'

// ---Local imports
import {
  setFilterData,
  setElementBounds,
  updateThemeVariable,
  switchTheme,
  loadStandalonePart,
  setPartLoaded,
  reloadPartFilters,
  setLoadedChunks,
} from './actions'
import {initializeTemplating} from './tcfg'

// ---Globals
const DDATA_MAX_HIST = 120 // we store effectly DDATA_MAX_HIST+1 (last DDATA_MAX_HIST + current)

const INITIAL_PROGRESS = {
  status: null,
  step: 0,
  remaining: [],
  done: [],
  total: 0,
  visible: false,
}
let handle = (action, reducer, initial = {}) => handleAction(action, reducer, initial)

let progress = handleActions(
  {
    INIT_PROGRESS: (meta, action) => {
      const {step, total, id, timestamp, steps} = action.payload

      let avg = l => (l.length > 0 ? sum(l) / l.length : 100) // 100ms default
      let avgs = dict(steps.map(e => [e, avg(get(meta.progressHistory, e, []))]))

      return {
        ...meta,
        progress: {
          ...INITIAL_PROGRESS,
          total,
          id,
          timestamp,
          remaining: steps,
          avgs,
          expectedDuration: Math.max(...steps.map(e => avgs[e])),
          status: 'initialized',
        },
      }
    },
    SET_PROGRESS_DURATION: (meta, action) => ({
      ...meta,
      progress: {...meta.progress, duration: action.payload.duration},
    }),
    PROGRESS_STEP: (meta, action) => {
      let COUNT = 5
      let {step} = action.payload
      let {progressHistory, progress} = meta
      let {timestamp, done, remaining} = progress

      // store duration of this step in history
      if (!has(progressHistory, step)) {
        progressHistory = {...progressHistory, [step]: []}
      }
      let duration = parseInt(now() - timestamp)
      if (duration) {
        progressHistory = {
          ...progressHistory,
          [step]: [...progressHistory[step], duration].slice(-COUNT),
        }
      }

      return {
        ...meta,
        progressHistory,
        progress: {
          ...progress,
          step: done.length + 1,
          done: [...done, step],
          // only remove first occurance of step in remaining
          remaining: [...remaining].sort(e => e == step).slice(0, -1),
          status: 'running',
        },
      }
    },
    SET_PROGRESS_VISIBLE: (meta, action) => ({
      ...meta,
      progress: {...meta.progress, visible: true},
    }),
    FINISH_PROGRESS: (meta, action) => {
      if (action.payload.id != meta.progress.id) {
        return meta
      }
      return {...meta, progress: {...INITIAL_PROGRESS, status: 'finished'}}
    },
  },
  {progress: {...INITIAL_PROGRESS, progressHistory: []}}
)

export const meta = [
  progress,
  handleAction(
    'MOUNT_PORTAL',
    (meta, action) => {
      return {...meta, portals: {...meta.portals, ...action.payload}}
    },
    {portals: {}}
  ),
  handleAction(
    updateThemeVariable,
    ({theme, ...meta}, {payload}) => {
      return {
        ...meta,
        theme: {...theme, modified: {...theme.modified, [payload.name]: payload.value}},
      }
    },
    {theme: {name: DEFAULT_THEME, modified: {}, variables: CSS_VAR_NAMES}}
  ),
  handleAction(
    switchTheme,
    ({theme, ...meta}, {payload}) => {
      return {
        ...meta,
        theme: {...theme, name: payload.name},
      }
    },
    {theme: {name: 'default', modified: {}, variables: CSS_VAR_NAMES}}
  ),
  handleAction(
    setElementBounds,
    ({elBounds, ...meta}, {payload}) => {
      return {
        ...meta,
        elBounds: {...elBounds, [payload.key]: payload.bounds},
      }
    },
    {elBounds: {}}
  ),
  handleAction('SET_DOMAIN_DATA', (meta, {payload}) => {
    /* we set the first domain context (always derived from meta.request) here
     */
    if (meta.request.dname) {
      // already initialized
      return meta
    }
    return {...meta, request: {...meta.request, dname: payload.name}}
  }),
  handleAction(
    loadStandalonePart,
    ({standaloneParts, ...meta}, {payload}) => {
      /* track loading, set to false to indicate loading
       */
      let {partStoreKey} = payload
      return {...meta, standaloneParts: {...standaloneParts, [partStoreKey]: false}}
    },
    {standaloneParts: {}}
  ),
  handleAction(
    setPartLoaded,
    ({standaloneParts, ...meta}, {payload}) => {
      /* track loading, set to runId to indicate loaded
       */
      let {partStoreKey, runId} = payload
      return {...meta, standaloneParts: {...standaloneParts, [partStoreKey]: runId}}
    },
    {}
  ),
  handleAction(
    setLoadedChunks,
    ({chunks, ...meta}, {payload}) => {
      /* register loaded chunks
       */
      return {...meta, chunks: {...chunks, loaded: [...chunks.loaded, ...payload.names]}}
    },
    {chunks: {loaded: []}}
  ),
  handleAction('REPLACE_QUERY_PARAMS', ({replacedQueryParams, ...meta}, {payload}) => ({
    ...meta,
    replacedQueryParams: {...replacedQueryParams, ...payload.params},
  })),
]

export const data = [
  handleAction(reloadPartFilters, ({apiPartsReload, ...data}, {payload}) => {
    let {part} = payload
    let old_val = apiPartsReload[part] || 0
    apiPartsReload = {...apiPartsReload, [part]: old_val + 1}
    return {...data, apiPartsReload}
  }),
  handleAction('ACTIVATE_VIEW', data => {
    return {...data, apiPartsReload: {}}
  }),

  handleAction(
    setFilterData,
    (data, action) => {
      let {runId, filterData} = action.payload
      let {newParts} = data

      if (newParts[runId]) {
        // merge
        let allPartNames = [...keys(newParts[runId]), ...keys(filterData)]
        filterData = dict(
          allPartNames.map(n => [n, {...get(newParts[runId], n, {}), ...get(filterData, n, {})}])
        )
      }

      return {
        ...data,
        // always write to newParts
        newParts: {...newParts, [runId]: filterData},
        // also write to parts if data for current runId
        parts: data.runId === runId ? filterData : data.parts,
      }
    },
    {
      // buffer until all sync parts loaded
      newParts: {},
      // copy newParts to parts on activateView
      parts: {},
      history: {},
    }
  ),
]

export const config = [
  handleAction(
    'SET_DOMAIN_DATA',
    (config, {payload}) => {
      let {dcode, ...ddata} = payload
      config = cacheDomainConfigs(
        {...config, ddata: updateDomainData(config.ddata, ddata, config.instance.itypes)},
        ddata.name
      )
      // also calculate templateconfig for domain, depends on:
      // - dconfig
      // - instance config
      // - (in ddata) embedded user.groups/client
      // - dcode (for all ancestors+self domains)
      config = {
        ...config,
        templating: {
          ...config.templating,
          [ddata.name]: initializeTemplating(config.instance, ddata, dcode),
        },
      }
      config = {...config, loading: {...config.loading, [payload.name]: false}}

      return config
    },
    {templating: {}}
  ),
  handleAction(
    'SET_DOMAIN_CSS',
    // update incoming dnames
    (config, {payload}) => ({...config, dcss: {...config.dcss, ...payload.dcss}}),
    {dcss: {}}
  ),
  handleAction(
    'INIT_DOMAIN',
    (config, {payload}) => {
      return {...config, loading: {...config.loading, [payload.dname]: true}}
    },
    {loading: {}}
  ),
]

let historyKey = router => {
  let p = router.pathname
  let q = ''
  if (size(router.query) > 0) {
    q = entries(router.query)
      .sort()
      .map(([k, v]) => `${k}=${v}`)
      .join('&')
  }
  return `${p}?${q}`
}

let updateHistory = (oldHistory, newPartData, router) => {
  // delete entries that are older than 1 day
  let filteredHistory = filterItems(
    oldHistory,
    (key, value) => value.timestamp >= new Date().getTime() - 86400000
  )

  // history entry limit of 50 => cut history to 49 entries to add the new
  if (size(filteredHistory) >= 50) {
    const sortedHistory = orderBy(filteredHistory, 'timestamp', 'asc')
    filteredHistory = sortedHistory.slice(0, 49)
  }

  filteredHistory = fromPairs(filteredHistory)
  return {
    ...filteredHistory,
    [historyKey(router)]: {
      data: newPartData,
      timestamp: new Date().getTime(),
    },
  }
}

let activateData = (data, router, runId) => {
  /* copy from newParts to parts */
  let newPartData = get(data.newParts, runId, {})
  let KEEP_LAST = 5 // in data.newParts
  let newParts = dict(filterItems(data.newParts, (k, v) => k > runId - KEEP_LAST))

  let history = updateHistory(data.history, newPartData, router)

  return {...data, history, parts: newPartData, runId, newParts}
}

let updateFilterHistory = (root, router, runId) => {
  /* if filterData gets integrated into current runId (lazy filters)
    then update history
  */

  if (root.data.runId !== runId) {
    return root
  }
  // XXX: we assume root.data.parts is updated earlier by setFilterData above
  // maybe it would be better to combine both filters
  let history = updateHistory(root.data.history, root.data.parts, router)
  return {...root, data: {...root.data, history}}
}

let cacheDomainConfigs = (config, dname) => {
  let keysDifferent = () =>
    hashObj(config.ddataCache[dname].cacheKeys) !== hashObj(config.ddata[dname].cacheKeys)

  if (!has(config.ddataCache, dname) || keysDifferent()) {
    log.debug('caching ddata for %o', dname)
    let entry = {...config.ddata[dname], _cached_: now()}

    let oldCache = config.ddataCache
    if (size(config.ddataCache) > DDATA_MAX_HIST) {
      oldCache = dict(
        sorted(entries(config.ddataCache), ([k, v]) => v._cached_).slice(-DDATA_MAX_HIST)
      )
    }
    let ddataCache = {...oldCache, [dname]: entry}
    config = {...config, ddataCache}
  }
  return config
}

let activateConfig = (config, router) => {
  let dname = decodeDomain(router.params || {}, config.domains, config.deploy.pathPrefix)
  if (!dname) {
    log.error('activateConfig: cannot decode dname %o', router.params)
    return config
  }
  return cacheDomainConfigs(config, dname)
}

let activateMeta = (meta, config, router) => {
  let pathname = cleanPathPrefix(config.deploy.pathPrefix, router.pathname)

  let request = {pathname, query: router.query}

  // custom injected config() access via enhancedActionReducer()
  let {isDefault, ...decoded} = decodeRequestWithAuth(router.params || {}, router.query, config)
  // copy over query params
  let copyQuery = dict(filterItems(decoded.query, (k, v) => !has(decoded, k)))

  request = {
    ...request,
    ...decoded,
    ...copyQuery,
  }

  // replacedQueryParams is overwritten via action REPLACE_QUERY_PARAMS
  return {...meta, request, replacedQueryParams: {}}
}

let activateView = (root, router, runId) => {
  /* make loaded view active
    - copy newParts to data.history
    - copy newParts to data.parts
    - copy ddata for current domain to ddataCache
  */
  if (root.meta.state !== 'APP_RUNNING') {
    return root
  }

  // newParts will be null, if restoreView has a cache miss
  // we come from an ROUTER_LOCATION_CHANGED after ROUTER_POP
  if (!runId && !root.data.newParts[runId]) {
    return root
  }

  let {data, meta, config} = root
  config = activateConfig(config, router)
  meta = activateMeta(meta, config, router)
  data = activateData(data, router, runId)

  return {...root, data, meta, config}
}

let updateDomainData = (prevDomainData, ddata, itypes) => {
  /* set ddata in store (either coming from server or from cache)
  add additional indexes
  XXX: this is also done for config.instance.itypes
  - byId (use this if you have an integer tuple byId[1][0].name == 'document')
  - byName (use this if you have a subtype name)
  - datatypeMap is also keyed by integer datatype
  */
  let types = ddata.types
  let {datatypeMap: datatypeMapByInt} = itypes
  let datatypeMap = dict(mapItems(datatypeMapByInt, (dt, dtname) => [dtname, parseInt(dt)]))
  let active_dt = filterItems(ddata.types, (k, v) => datatypeMap[k] !== undefined)

  // XXX: we still dont rename the st.name attribute
  let renameSubtypes = sts => dict(mapItems(sts, (name, st) => [name.replace(/ /g, '_'), st]))
  let renamedDatatypes = dict(active_dt.map(([k, v]) => [k, renameSubtypes(v)]))

  let byName = {}
  let handleSubtypes = (sts, datatypeName, internalDatatype) =>
    dict(
      mapItems(sts, (k2, v2) => {
        let type = {...v2, datatypeName, internalDatatype}
        byName[v2.name.replace(/ /g, '_')] = type
        return [v2.internalType, type]
      })
    )
  let byId = dict(active_dt.map(([k, v]) => [datatypeMap[k], handleSubtypes(v, k, datatypeMap[k])]))
  datatypeMap = {...datatypeMap, ...datatypeMapByInt}

  ddata = {...ddata, types: {...types, ...renamedDatatypes, byName, byId, datatypeMap}}

  // also add ddata with odid key
  return {...prevDomainData, [ddata.name]: ddata, [ddata.id]: ddata}
}

let restoreView = (root, router) => {
  /* restore view from history/caches
  on cache-miss, no newParts will be written and next activateView will be skipped
  */
  let {data, config, meta} = root

  let {runId} = GENERATOR('runId')()

  let dname = decodeDomain(router.params || {}, config.domains, config.deploy.pathPrefix)
  if (!config.ddata[dname]) {
    if (config.ddataCache[dname]) {
      config = {
        ...config,
        ddata: updateDomainData(config.ddata, config.ddataCache[dname], config.instance.itypes),
      }
    } else {
      log.info('cache miss [ddata] for %o', router.pathname)
      return {...root, data: {...data, poppedRunId: runId}}
    }
  }

  // restore filter data
  let key = historyKey(router)
  let historyEntry = get(root, `data.history[${key}]`, {})

  if (!isEmpty(historyEntry)) {
    data = {
      ...data,
      history: {
        ...data.history,
        // update timestamp in history to prevent immediate deletion because of entry limit
        [key]: {
          ...historyEntry,
          timestamp: new Date().getTime(),
        },
      },
      newParts: {...data.newParts, [runId]: historyEntry.data},
      runId,
    }
  } else {
    log.info('cache miss [filter history] for %o', router.pathname)
    return {...root, data: {...data, poppedRunId: runId}}
  }

  return {...root, config, data}
}

let resetViewCaches = (root, appstate) => {
  /* before server chooser */
  if (appstate !== 'INIT_APP_ERROR') {
    return root
  }
  return {
    ...root,
    config: {...root.config, ddataCache: {}},
    data: {...root.data, history: {}},
  }
}
export const root = [
  handle('ROUTER_POP', (root, action) => restoreView(root, action.payload)),
  // we get payload.router if triggered via ROUTER_LOCATION_CHANGED on redux middleware
  handle('ACTIVATE_VIEW', (root, {payload}) =>
    activateView(root, payload.router || root.router, payload.runId)
  ),
  handle(setFilterData, (root, {payload}) => updateFilterHistory(root, root.router, payload.runId)),

  handle('SET_APP_STATE', (root, action) => resetViewCaches(root, action.payload.state)),
  handle('DISPATCH_ROUTE', (root, {payload}) => ({
    ...root,
    router: {...root.router, params: payload.params},
  })),
]
